/**
 * Virtual d-pad + 确认/取消/菜单 buttons for touch devices (auto-detected, or forced with ?touch=1 /
 * disabled with ?touch=0). Emits the abstract input events on the bus; plain taps on the world
 * (not on any UI element) emit input:tapWorld with world coordinates from the active world camera.
 */
import Phaser from 'phaser';
import { GAME_H, GAME_W } from '@/core/config';
import { bus, Events } from '@/core/events';
import type { UiRoot } from './UiRoot';
import { uiText } from './text';
import { Css, Slices, UiDepth } from './theme';

type Dir = -1 | 0 | 1;

export function shouldEnableTouch(scene: Phaser.Scene): boolean {
  try {
    const p = new URLSearchParams(location.search).get('touch');
    if (p === '1') return true;
    if (p === '0') return false;
  } catch { /* no location */ }
  const device = scene.sys.game.device;
  return Boolean(device.input.touch) && !device.os.desktop;
}

export class TouchControls {
  readonly container: Phaser.GameObjects.Container;
  private enabled = false;
  private dpadPointer: number | null = null;
  private dir: { x: Dir; y: Dir } = { x: 0, y: 0 };
  private readonly dpad: Phaser.GameObjects.Image;
  private readonly dpadZone: Phaser.GameObjects.Zone;
  private readonly center = { x: 72, y: GAME_H - 72 };
  private consumed = new Set<number>();

  constructor(private readonly root: UiRoot, private readonly tapSuppressed: () => boolean) {
    const s = root.scene;
    s.input.addPointer(3);
    this.container = s.add.container(0, 0).setDepth(UiDepth.TOUCH).setVisible(false).setAlpha(0.72);

    this.dpad = s.add.image(this.center.x, this.center.y, 'ui-dpad').setOrigin(0.5);
    this.dpadZone = s.add.zone(this.center.x, this.center.y, 144, 144).setOrigin(0.5).setInteractive(new Phaser.Geom.Circle(72, 72, 72), Phaser.Geom.Circle.Contains);
    this.dpadZone.on('pointerdown', (p: Phaser.Input.Pointer) => { this.consumed.add(p.id); this.dpadPointer = p.id; this.updateDir(p); });
    this.container.add([this.dpad, this.dpadZone]);

    this.container.add(this.button(GAME_W - 52, GAME_H - 60, 26, '确认', () => bus.emitTyped(Events.INPUT_CONFIRM, { source: 'touch' })));
    this.container.add(this.button(GAME_W - 104, GAME_H - 38, 20, '取消', () => bus.emitTyped(Events.INPUT_CANCEL, { source: 'touch' })));
    this.container.add(this.pill(GAME_W - 40, 16, '菜单', () => bus.emitTyped(Events.INPUT_MENU, { source: 'touch' })));

    root.onPointer('pointermove', (...args) => { const p = args[0] as Phaser.Input.Pointer; if (this.enabled && p.id === this.dpadPointer && p.isDown) this.updateDir(p); });
    root.onPointer('pointerup', (...args) => this.onPointerUp(args[0] as Phaser.Input.Pointer, (args[1] as Phaser.GameObjects.GameObject[] | undefined) ?? []));
    root.onPointer('pointerupoutside', (...args) => this.onPointerUp(args[0] as Phaser.Input.Pointer, []));
    root.track(() => { this.release(); this.container.destroy(true); });
  }

  isEnabled(): boolean { return this.enabled; }

  setEnabled(on: boolean): void {
    if (on === this.enabled) return;
    this.enabled = on;
    this.container.setVisible(on);
    if (!on) this.release();
  }

  currentDir(): { x: Dir; y: Dir } { return { ...this.dir }; }

  private button(x: number, y: number, r: number, label: string, fn: () => void): Phaser.GameObjects.GameObject[] {
    const s = this.root.scene;
    const img = s.add.image(x, y, 'ui-round-btn').setOrigin(0.5).setDisplaySize(r * 2, r * 2);
    const t = uiText(s, x, y, label, { fontSize: '11px', color: Css.parchment }).setOrigin(0.5);
    const zone = s.add.zone(x, y, r * 2 + 8, r * 2 + 8).setOrigin(0.5).setInteractive(new Phaser.Geom.Circle(r + 4, r + 4, r + 4), Phaser.Geom.Circle.Contains);
    zone.on('pointerdown', (p: Phaser.Input.Pointer) => { this.consumed.add(p.id); img.setScale(img.scaleX * 0.9, img.scaleY * 0.9); fn(); });
    zone.on('pointerup', () => img.setDisplaySize(r * 2, r * 2));
    zone.on('pointerout', () => img.setDisplaySize(r * 2, r * 2));
    return [img, t, zone];
  }

  private pill(x: number, y: number, label: string, fn: () => void): Phaser.GameObjects.GameObject[] {
    const s = this.root.scene;
    const bar = s.add.nineslice(x, y, Slices.barDim.key, undefined, 56, 22, Slices.barDim.l, Slices.barDim.r, Slices.barDim.t, Slices.barDim.b).setOrigin(0.5);
    const t = uiText(s, x, y, label, { fontSize: '11px', color: Css.parchment }).setOrigin(0.5);
    const zone = s.add.zone(x, y, 64, 30).setOrigin(0.5).setInteractive();
    zone.on('pointerdown', (p: Phaser.Input.Pointer) => { this.consumed.add(p.id); fn(); });
    return [bar, t, zone];
  }

  private updateDir(p: Phaser.Input.Pointer): void {
    const dx = p.x - this.center.x;
    const dy = p.y - this.center.y;
    const len = Math.hypot(dx, dy);
    let x: Dir = 0, y: Dir = 0;
    if (len >= 10) {
      if (Math.abs(dx) > len * 0.38) x = dx > 0 ? 1 : -1;
      if (Math.abs(dy) > len * 0.38) y = dy > 0 ? 1 : -1;
    }
    this.emitDir(x, y);
  }

  private emitDir(x: Dir, y: Dir): void {
    if (x === this.dir.x && y === this.dir.y) return;
    this.dir = { x, y };
    bus.emitTyped(Events.INPUT_DIR, { x, y, source: 'touch' });
  }

  private release(): void {
    this.dpadPointer = null;
    this.emitDir(0, 0);
  }

  private onPointerUp(p: Phaser.Input.Pointer, over: Phaser.GameObjects.GameObject[]): void {
    if (p.id === this.dpadPointer) this.release();
    const consumed = this.consumed.delete(p.id);
    if (consumed || over.length > 0) return;
    if (this.tapSuppressed() || this.root.hasModal()) return;
    if (p.getDistance() > 12 || p.getDuration() > 350) return;
    const world = this.root.worldScene();
    if (!world) return;
    const cam = world.cameras.main;
    const wp = cam.getWorldPoint(p.x, p.y);
    bus.emitTyped(Events.INPUT_TAP_WORLD, { worldX: Math.round(wp.x), worldY: Math.round(wp.y) });
  }
}
